import { Search } from "@mui/icons-material";
import { Box, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, MenuItem, Pagination, Select, Skeleton, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { AttachmentImage, SelectBox } from "../../components";
import { ReactSelectOption, SelectBoxOption } from "../../interfaces";
import { useGetUoMDropDownQuery, useLazyGetNounsQuery } from "../../redux/services";

const defaultNoun = {_id: null, label: "All", value: null, noun_name: "All"};

export interface NounDataInterface { data: any[], isLoading: boolean, isError: boolean, isSuccess: boolean, isFetching: boolean }

export const NounListing: React.FC<{ value: any, onChange: any, region: SelectBoxOption, setRegion: any }> = ({ value, onChange, region, setRegion }) => {

    const uomData = useGetUoMDropDownQuery({});
    const [getNouns, nounD] = useLazyGetNounsQuery();

    const nounData = { ...nounD, data: nounD?.data?.data ?? [], currentData: nounD?.data?.data ?? [], totalCount: nounD?.data?.totalCount };
    const { data, isLoading, isError, isSuccess, isFetching, totalCount } = nounData;

    const [search, setSearch] = React.useState("");
    const [showSearch, setShowSearch] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        onChange(defaultNoun);
    };

    const toogleShowSearch = () => setShowSearch(!showSearch);

    const fetchNouns = () => {
        getNouns({ page: page - 1, pageSize: rowsPerPage, search, uom_id: `${region?.value ?? ""}` })
    }

    React.useEffect(() => {
        if (isSuccess && !isLoading && !isError && !value) {
            if (region?.value) {
                onChange(data?.filter((_: any) => _.uom_id === region.value)?.[0] ?? data?.[0] ?? {});
            } else {
                onChange(data?.[0] ?? {});
            }
        }
        // eslint-disable-next-line
    }, [isSuccess, isFetching, value]);

    React.useEffect(() => {
        fetchNouns();
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        fetchNouns();
        // eslint-disable-next-line
    }, [page, rowsPerPage, region])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            fetchNouns();
        }, 500)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const nouns = [defaultNoun, ...data];

    return (
        <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, marginRight: { xs: 0, sm: 1 }, borderRadius: 1, position: "relative", height: "calc(100vh - 216px)", overflow: "hidden" }}>
            {/* Heading */}
            <Box
                sx={{
                    padding: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                <Typography variant="subtitle2" sx={{ fontFamily: "htrts_bold" }}>{`Nouns (${totalCount ?? 0})`}</Typography>
                <IconButton size="small" onClick={toogleShowSearch}>
                    <Search fontSize="small" htmlColor="black" />
                </IconButton>
            </Box>

            <Box
                sx={{
                    px: 1,
                    paddingBottom: 1
                }}>
                <SelectBox
                    loading={uomData.isLoading || uomData.isFetching}
                    options={uomData.data ? [{ value: '', label: 'All' }, ...(uomData.data as unknown as ReactSelectOption[])] : []}
                    label={"Region"}
                    value={region}
                    onChange={(data: any) => setRegion(data)}
                />

            </Box>

            {/* Search Bar */}
            {showSearch && <TextField
                sx={{ paddingX: 1, paddingBottom: 1 }}
                variant="standard"
                placeholder="Search noun"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size={"small"}
                fullWidth
            />}

            {(isLoading || isFetching) && <Stack
                sx={{
                    gap: 1,
                    p: 1
                }}>
                {Array(10).fill(0).map((_: number, key: number) => {
                    return <Skeleton height={"70px"} variant="rectangular" />
                })}
            </Stack>}

            <List component="nav" dense sx={{ height: "80%", overflow: "auto", paddingBottom: "70px" }}>
                {nouns.map((noun: any, key: number) => {
                    return <ListItem
                        id={`noun-${key}`}
                        key={key}
                        dense
                        divider
                    >
                        <ListItemButton selected={value?._id === noun?._id} onClick={(event) => onChange(noun)}>
                            <ListItemAvatar>
                                <AttachmentImage object_id={noun._id} object_type="noun" {...(noun?.attachments?.[0] ?? {})} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={noun?.uom_name ? `${noun.noun_name} (${noun?.uom_name})` : noun?.noun_name}
                                secondary={noun.group_name}
                            />
                        </ListItemButton>
                    </ListItem>
                })}
            </List>

            <Stack
                spacing={1}
                direction={"row"}
                sx={{
                    mt: 2,
                    p: 2,
                    justifyContent: "space-between",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.background.paper,
                    boxShadow: `rgba(0, 0, 0, 0.06) 0px 2px 4px 0px inset`
                }}>
                <Select label={"Page Size"} size="small" variant="standard" value={rowsPerPage} onChange={(e) => setRowsPerPage(parseInt(`${e.target.value}`))}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={150}>150</MenuItem>
                    <MenuItem value={200}>200</MenuItem>
                </Select>
                <Pagination size="small" siblingCount={0} boundaryCount={1} color="primary" variant="outlined" shape="rounded" count={Math.ceil((totalCount ?? 0) / rowsPerPage)} page={page} onChange={handleChange} />
            </Stack>
        </Box>
    );
}